import type { FC, ReactNode } from 'react'

import {
    Briefcase,
    CalendarClock,
    Monitor,
    Smile
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Container } from '@/components/generals/container'
import { SectionHeading } from '@/components/generals/section-heading'

import { SCHEDULE_OPTIONS } from '@/constants/site'

const SEATS = 12

const FEATURES = [
    {
        icon: Monitor,
        title: "One computer per student",
        text: "No sharing screens and no waiting your turn. You practise every single step yourself."
    },
    {
        icon: Smile,
        title: "Patient instructors",
        text: "Trainers who use these skills at work and explain them in plain, friendly language."
    },
    {
        icon: Briefcase,
        title: "Career support",
        text: "Help with your CV, your LinkedIn profile and interview practice when you finish."
    }
]

export const WhyUsSection: FC = (): ReactNode => {
    return (
        <section id='why-us' aria-labelledby='why-us-title' className='py-20 sm:py-28'>
            <Container>
                <SectionHeading
                    id='why-us-title'
                    eyebrow="Why ByteWise"
                    title="Built for people who learn by doing"
                    description="Whether you are touching a computer for the first time or adding skills for a promotion, every class is designed around practice, not long lectures."
                />

                <div className='mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3'>
                    <div className='relative overflow-hidden rounded-3xl bg-primary p-7 text-primary-foreground sm:p-9 md:col-span-2'>
                        <div
                            aria-hidden='true'
                            className='bg-dots pointer-events-none absolute inset-0 text-primary-foreground/35 [mask-image:radial-gradient(ellipse_at_top_right,black,transparent_70%)]'
                        />

                        <div className='relative grid gap-8 sm:grid-cols-[auto_1fr] sm:items-end'>
                            <div>
                                <p className='font-heading text-8xl leading-none font-semibold tracking-tight sm:text-9xl'>
                                    {SEATS}
                                </p>
                                <p className='mt-3 font-mono text-xs tracking-wider text-primary-foreground/70 uppercase'>
                                    Students per class, at most
                                </p>
                            </div>

                            <div>
                                <h3 className='text-2xl font-semibold sm:text-3xl'>
                                    Small classes, so nobody gets left behind
                                </h3>

                                <p className='mt-3 max-w-md text-primary-foreground/80'>
                                    Your instructor knows your name, sees your screen and has time to answer every question, even the ones you think are silly.
                                </p>

                                <div className='mt-6 grid max-w-xs grid-cols-6 gap-2' aria-hidden='true'>
                                    {Array.from({ length: SEATS }, (_, index) => (
                                        <span
                                            key={index}
                                            className={cn(
                                                'flex aspect-square items-center justify-center rounded-lg',
                                                index < 9 ? 'bg-accent text-accent-foreground' : 'bg-primary-foreground/15 text-primary-foreground/60'
                                            )}
                                        >
                                            <Monitor className='size-3.5' />
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    <FeatureTile {...FEATURES[0]} />

                    <div className='rounded-3xl border bg-card p-7 sm:p-8'>
                        <span className='flex size-11 items-center justify-center rounded-xl bg-secondary text-primary'>
                            <CalendarClock className='size-5' aria-hidden='true' />
                        </span>

                        <h3 className='mt-6 text-xl font-semibold'>
                            Timings that fit your life
                        </h3>

                        <p className='mt-2 text-sm leading-relaxed text-muted-foreground'>
                            Study before work, after work or at the weekend, in our classroom or live online from home.
                        </p>

                        <ul className='mt-5 flex flex-wrap gap-2'>
                            {SCHEDULE_OPTIONS.map(option => (
                                <li key={option} className='rounded-full border bg-background px-3 py-1 text-xs font-medium'>
                                    {option}
                                </li>
                            ))}
                        </ul>
                    </div>

                    <FeatureTile {...FEATURES[1]} />
                    <FeatureTile {...FEATURES[2]} />
                </div>
            </Container>
        </section>
    )
}

interface FeatureTileProps {
    icon: typeof Monitor
    title: string
    text: string
}

const FeatureTile: FC<FeatureTileProps> = ({ icon: Icon, title, text }): ReactNode => {
    return (
        <div className='rounded-3xl border bg-card p-7 sm:p-8'>
            <span className='flex size-11 items-center justify-center rounded-xl bg-secondary text-primary'>
                <Icon className='size-5' aria-hidden='true' />
            </span>

            <h3 className='mt-6 text-xl font-semibold'>
                {title}
            </h3>

            <p className='mt-2 text-sm leading-relaxed text-muted-foreground'>
                {text}
            </p>
        </div>
    )
}
